import { Fragment, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@/components/flight/FlightMap.css';
import './LiveFlightsMap.css';
import DarkBasemap from '@/components/maps/DarkBasemap';
import { FLIGHT_MAP_COLORS, flightMapStyle } from '@/utils/flightMapStyle';
import { airportPoint, planeFor } from '@/utils/liveFlights';

/**
 * Every Defera traveler in the air, on the trip page's flight map look
 * (src/components/flight/FlightMap.jsx, src/utils/flightMapStyle.js): the
 * deep-navy chart with lit coastlines, each flight's flown route lit and the
 * rest dashed, the plane on its glow with its flight number. Unlike the trip
 * page's picture this map is a tool: drag, pinch and the zoom buttons work
 * (the scroll wheel stays with the page). A tap on a plane selects it; the
 * selected flight is drawn brightest and the map flies to it. Flights about
 * to leave show as faint dashed lines between airports we have coordinates
 * for. The map frames the planes in the air (or, with none, the flights
 * about to leave).
 */

const PLANE = 'M12 1.5 13.3 8.6 21.5 13.1v1.6l-8.2-2.1-.5 5.7 2.6 2v1.3L12 20.4l-3.4 1.2v-1.3l2.6-2-.5-5.7-8.2 2.1v-1.6l8.2-4.5z';
const WORLD = L.latLngBounds([[-10, -150], [62, 40]]);

const AHEAD = { color: '#7dd3fc', opacity: 0.45, weight: 1.5, dashArray: '2 6', lineCap: 'round' };
const FLOWN = [
  { color: FLIGHT_MAP_COLORS.coast, opacity: 0.16, weight: 10 },
  { color: FLIGHT_MAP_COLORS.coast, opacity: 0.4, weight: 4.5 },
  { color: '#f0f9ff', opacity: 0.95, weight: 1.75 },
].map((line) => ({ ...line, lineCap: 'round', lineJoin: 'round' }));
const SOON = { color: '#7dd3fc', opacity: 0.22, weight: 1.25, dashArray: '1 7', lineCap: 'round' };

const dim = (style, on) => (on ? style : { ...style, opacity: style.opacity * 0.45 });
const safe = (s) => String(s || '').replace(/[^A-Z0-9 ]/gi, '');

function planeIcon(heading, label, selected) {
  return L.divIcon({
    className: 'live-flight-plane',
    html: `<div class="live-flight-plane-wrap${selected ? ' is-selected' : ''}">
      <div class="flight-map-plane-body" style="transform:rotate(${Math.round(heading || 0)}deg)">
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="${PLANE}" fill="#f0f9ff"/></svg>
      </div>
      <span class="live-flight-plane-label">${safe(label)}</span>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function airportIcon(code, { arriving = false, faint = false } = {}) {
  return L.divIcon({
    className: 'flight-map-airport',
    html: `<div class="flight-map-airport-pin${arriving ? ' is-arriving' : ''}${faint ? ' is-faint' : ''}">
      <span class="flight-map-airport-dot"></span>
      <span class="flight-map-airport-code">${safe(code)}</span>
    </div>`,
    iconSize: [44, 30],
    iconAnchor: [22, 5],
  });
}

/** Frame every flight when the set of flights changes; fly to the selected one. */
function Framing({ points, flightsKey, selectedPoint, selectedId }) {
  const map = useMap();
  const framedKey = useRef(null);
  useEffect(() => {
    if (framedKey.current === flightsKey) return undefined;
    // After layout settles (the list under the map arrives with the flights).
    // Marked framed only once it has happened, so a re-render that cancels
    // this frame schedules it again.
    const timer = setTimeout(() => {
      framedKey.current = flightsKey;
      map.invalidateSize();
      // No animation: framing isn't news, and an animation waits on the tab
      // being visible.
      if (points.length >= 2) map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 6, animate: false });
      else if (points.length === 1) map.setView(points[0], 5, { animate: false });
      else map.fitBounds(WORLD, { animate: false });
    }, 0);
    return () => clearTimeout(timer);
  }, [map, points, flightsKey]);
  // Fly only when the selection changes, not each time the plane moves.
  const pointRef = useRef(selectedPoint);
  useEffect(() => {
    pointRef.current = selectedPoint;
  });
  useEffect(() => {
    if (selectedId && pointRef.current) map.flyTo(pointRef.current, Math.max(map.getZoom(), 5), { duration: 0.8 });
  }, [map, selectedId]);
  return null;
}

export default function LiveFlightsMap({ inAir = [], departing = [], selectedId = null, onSelect = () => {}, nowMs = Date.now(), className = '' }) {
  const drawn = useMemo(
    () => inAir.map((flight) => ({ flight, plane: planeFor(flight, nowMs) })).filter((d) => d.plane),
    [inAir, nowMs],
  );
  const soon = useMemo(
    () => departing
      .map((flight) => ({ flight, from: airportPoint(flight.origin), to: airportPoint(flight.destination) }))
      .filter((d) => d.from && d.to),
    [departing],
  );
  // Frame the planes in the air; only with none, the flights about to leave.
  const points = useMemo(() => (drawn.length
    ? drawn.flatMap((d) => [d.plane.route[0], d.plane.route[d.plane.route.length - 1], d.plane.point])
    : soon.flatMap((d) => [d.from, d.to])), [drawn, soon]);
  const flightsKey = useMemo(() => [...inAir, ...departing].map((f) => f.id).sort().join('|'), [inAir, departing]);
  const selected = drawn.find((d) => d.flight.id === selectedId) || null;
  const anySelected = Boolean(selected);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <MapContainer
        className="flight-map live-flights-map"
        bounds={WORLD}
        style={{ height: '100%', width: '100%', zIndex: 0, background: FLIGHT_MAP_COLORS.sea }}
        zoomControl
        scrollWheelZoom={false}
        worldCopyJump
        minZoom={2}
        attributionControl
      >
        <DarkBasemap restyle={flightMapStyle} />
        <Framing points={points} flightsKey={flightsKey} selectedPoint={selected?.plane.point || null} selectedId={selectedId} />

        {soon.map(({ flight, from, to }) => (
          <Polyline key={`soon-${flight.id}`} positions={[from, to]} pathOptions={SOON} interactive={false} />
        ))}

        {drawn.map(({ flight, plane }) => {
          const on = !anySelected || flight.id === selectedId;
          return (
            <Fragment key={flight.id}>
              <Polyline positions={plane.ahead} pathOptions={dim(AHEAD, on)} interactive={false} />
              {FLOWN.map((line) => (
                <Polyline key={line.weight} positions={plane.flown} pathOptions={dim(line, on)} interactive={false} />
              ))}
              <Marker position={plane.route[0]} icon={airportIcon(flight.origin, { faint: !on })} interactive={false} keyboard={false} />
              <Marker position={plane.route[plane.route.length - 1]} icon={airportIcon(flight.destination, { arriving: on, faint: !on })} interactive={false} keyboard={false} />
              <Marker
                position={plane.point}
                icon={planeIcon(plane.heading, flight.flight, flight.id === selectedId)}
                zIndexOffset={flight.id === selectedId ? 2000 : 1000}
                eventHandlers={{ click: () => onSelect(flight.id) }}
                title={`${flight.flight} ${flight.origin} to ${flight.destination}`}
              />
            </Fragment>
          );
        })}
      </MapContainer>
      {drawn.length === 0 && (
        <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center">
          <span className="rounded-full border border-sky-400/15 bg-[#040810]/80 px-4 py-2 text-[12px] sm:text-[13px] text-sky-100/80">
            No Defera travelers in the air right now
          </span>
        </div>
      )}
    </div>
  );
}

LiveFlightsMap.propTypes = {
  inAir: PropTypes.arrayOf(PropTypes.object),
  departing: PropTypes.arrayOf(PropTypes.object),
  selectedId: PropTypes.string,
  onSelect: PropTypes.func,
  nowMs: PropTypes.number,
  className: PropTypes.string,
};

Framing.propTypes = {
  points: PropTypes.array.isRequired,
  flightsKey: PropTypes.string.isRequired,
  selectedPoint: PropTypes.array,
  selectedId: PropTypes.string,
};
